import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { formatINR } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFoodPricing = () => {
  const { getToken } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    min_guarantee: 50,
    base_rate: 0,
    additional_rate: 0,
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${API}/food-tiers`);
      setTiers(response.data);
    } catch (error) {
      toast.error("Failed to load food tiers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTier) {
        await axios.put(`${API}/food-tiers/${editingTier.id}`, formData, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Food tier updated");
      } else {
        await axios.post(`${API}/food-tiers`, formData, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success("Food tier created");
      }
      
      setShowModal(false);
      setEditingTier(null);
      resetForm();
      fetchTiers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleEdit = (tier) => {
    setEditingTier(tier);
    setFormData({
      name: tier.name,
      min_guarantee: tier.min_guarantee,
      base_rate: tier.base_rate,
      additional_rate: tier.additional_rate,
      is_active: tier.is_active,
      sort_order: tier.sort_order
    });
    setShowModal(true);
  };

  const handleDelete = async (tierId) => {
    if (!window.confirm("Are you sure you want to delete this tier?")) return;
    
    try {
      await axios.delete(`${API}/food-tiers/${tierId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Food tier deleted");
      fetchTiers();
    } catch (error) {
      toast.error("Failed to delete tier");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      min_guarantee: 50,
      base_rate: 0,
      additional_rate: 0,
      is_active: true,
      sort_order: 0
    });
  };

  const openCreateModal = () => {
    setEditingTier(null);
    resetForm();
    setShowModal(true);
  };

  if (loading) {
    return (
      <AdminLayout title="Food Pricing">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Food Pricing">
      <div className="space-y-6" data-testid="food-pricing-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground">
              Manage food packages and pricing tiers
            </p>
            <p className="text-sm text-amber-600 mt-1">
              ⚠️ Rates are per head per day and auto-scale based on duration.
            </p>
          </div>
          <Button
            onClick={openCreateModal}
            data-testid="add-tier-btn"
            className="bg-[#B08968] hover:bg-[#9A7558] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Tier
          </Button>
        </div>

        {/* Tiers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              data-testid={`tier-card-${tier.id}`}
              className={`bg-white rounded-2xl border border-border/40 p-6 shadow-elegant ${
                !tier.is_active ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-[#2D2D2D]">{tier.name}</h3>
                  {!tier.is_active && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(tier)}
                    data-testid={`edit-tier-${tier.id}`}
                    className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-[#B08968]"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(tier.id)}
                    data-testid={`delete-tier-${tier.id}`}
                    className="p-2 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Min Guarantee</span>
                  <span className="font-medium">{tier.min_guarantee} pax</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base Rate</span>
                  <span className="font-medium text-[#B08968]">{formatINR(tier.base_rate)}/head/day</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Additional Rate</span>
                  <span className="font-medium">{formatINR(tier.additional_rate)}/head/day</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {tiers.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p>No food tiers configured yet.</p>
            <Button
              onClick={openCreateModal}
              variant="link"
              className="text-[#B08968]"
            >
              Create your first tier
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="tier-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingTier ? "Edit Food Tier" : "Create Food Tier"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="form-group">
              <Label htmlFor="tier-name">Tier Name</Label>
              <Input
                id="tier-name"
                data-testid="tier-name-input"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., 50 Pax Tier"
              />
            </div>
            
            <div className="form-group">
              <Label htmlFor="min-guarantee">Minimum Guarantee (pax)</Label>
              <Input
                id="min-guarantee"
                data-testid="min-guarantee-input"
                type="number"
                required
                value={formData.min_guarantee}
                onChange={(e) => setFormData({...formData, min_guarantee: parseInt(e.target.value) || 0})}
              />
            </div>
            
              <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label htmlFor="base-rate">Base Rate (₹/head/day)</Label>
                <Input
                  id="base-rate"
                  data-testid="base-rate-input"
                  type="number"
                  required
                  value={formData.base_rate}
                  onChange={(e) => setFormData({...formData, base_rate: parseFloat(e.target.value) || 0})}
                />
                <p className="text-xs text-muted-foreground mt-1">Per head per day rate</p>
              </div>
              
              <div className="form-group">
                <Label htmlFor="additional-rate">Additional Rate (₹/head/day)</Label>
                <Input
                  id="additional-rate"
                  data-testid="additional-rate-input"
                  type="number"
                  required
                  value={formData.additional_rate}
                  onChange={(e) => setFormData({...formData, additional_rate: parseFloat(e.target.value) || 0})}
                />
                <p className="text-xs text-muted-foreground mt-1">For guests above min guarantee</p>
              </div>
            </div>
            
            <div className="form-group">
              <Label htmlFor="sort-order">Sort Order</Label>
              <Input
                id="sort-order"
                data-testid="sort-order-input"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="is-active">Active</Label>
              <Switch
                id="is-active"
                data-testid="is-active-switch"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-tier-btn"
                className="flex-1 bg-[#B08968] hover:bg-[#9A7558] text-white"
              >
                {editingTier ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminFoodPricing;
